'use client';

// #region Global Imports
import cx from 'classnames';
import Link from 'next/link';
import * as React from 'react';
// #endregion Global Imports
// #region Local Imports
import { Icon } from '@components/Icon';
import { gql } from '@generated/gql';
import type { IProps } from '@components/PostContent/Toc/Toc';
import { getFragmentData } from '@generated/fragment-masking';
import { flatToTree } from '@lib/func/flatToTree';
import { renderBlockContentTranslation } from '@lib/func/renderBlockContentTranslation';
import styles from './Toc.module.sass';
import get from 'lodash/get';

// #endregion Local Imports

interface TocItem {
  id: string;
  text: string;
  children: [TocItem];
}

const TocList: React.FunctionComponent<{ data: [TocItem] }> = ({ data }) => (
  <ul>
    {data.map((item, i: number) => (
      <li key={i}>
        <Link href={`#${item?.id}`} scroll={false}>
          {item.text}
        </Link>
        <TocList data={item?.children ?? ''} />
      </li>
    ))}
  </ul>
);

export const Toc: React.FC<IProps.Toc> = ({ data: _data }) => {
  const data = getFragmentData(TermTocFragment, _data);

  const termOverview = data?.termOverview;
  const termContent = data?.termContent;
  const termTocToTop = data?.termTocToTop;

  const entity = get(data, ['route', 'entity']);

  // filter out all the titles
  const titles:
    | [
        {
          id: string;
          level: number;
          text: string;
        },
      ]
    | undefined = entity?.paragraphs
    ?.filter(
      (item: any) => item?.entity?.translation?.__typename === 'ParagraphTitle',
    )
    // map them to a simple flat array
    .map((item: any) => ({
      id: item?.entity?.uuid,
      level: parseInt(item?.entity?.translation?.level) - 2, // entry point is h2
      text: item?.entity?.translation?.title,
    }));

  // Build the Tree out of it
  const titlesTree = flatToTree(titles).tree;

  return (
    <div className={styles.Toc__root}>
      <h4 className={cx(styles.Toc__subtitle, 'h1-small')}>
        {renderBlockContentTranslation(termOverview)}
      </h4>
      <h4 className={cx(styles.Toc__title, 'h1')}>
        {renderBlockContentTranslation(termContent)}
      </h4>
      <div className={styles.Toc__content}>
        <div className={styles.Toc__content_inner}>
          <TocList data={titlesTree} />
          <div className={styles.Toc__up}>
            <Link href="#index" scroll={false}>
              <div className={styles.Toc__icon}>
                <Icon icon="up" inline />
              </div>
              <span>
                {renderBlockContentTranslation(termTocToTop) ?? 'Go to top'}
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TermTocFragment = gql(/* GraphQL */ `
  fragment TermTocFragment on Query {
    termTocToTop: blockContentQuery(
      filter: {
        conjunction: AND
        conditions: [
          { field: "type", value: "translations", language: $language }
          { field: "info", value: "Post go to top" }
        ]
      }
    ) {
      translation: entities {
        value: entityTranslation(language: $language) {
          ... on BlockContentTranslations {
            text: fieldPlaintext
          }
        }
      }
    }

    termTocPostSimilar: blockContentQuery(
      filter: {
        conjunction: AND
        conditions: [
          { field: "type", value: "translations", language: $language }
          { field: "info", value: "Post similar" }
        ]
      }
    ) {
      translation: entities {
        value: entityTranslation(language: $language) {
          ... on BlockContentTranslations {
            text: fieldPlaintext
          }
        }
      }
    }

    termContent: blockContentQuery(
      filter: {
        conjunction: AND
        conditions: [
          { field: "type", value: "translations", language: $language }
          { field: "info", value: "Content" }
        ]
      }
    ) {
      translation: entities {
        value: entityTranslation(language: $language) {
          ... on BlockContentTranslations {
            text: fieldPlaintext
          }
        }
      }
    }

    termOverview: blockContentQuery(
      filter: {
        conjunction: AND
        conditions: [
          { field: "type", value: "translations", language: $language }
          { field: "info", value: "Overview" }
        ]
      }
    ) {
      translation: entities {
        value: entityTranslation(language: $language) {
          ... on BlockContentTranslations {
            text: fieldPlaintext
          }
        }
      }
    }

    route(path: $path) {
      ... on EntityCanonicalUrl {
        entity {
          ... on NodePost {
            paragraphs: fieldContent {
              entity {
                uuid: entityUuid
                translation: entityTranslation(language: $language) {
                  ... on ParagraphTitle {
                    title: fieldTitle
                    level: fieldLevel
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`);
